import { useCallback, useEffect, useState } from 'react';
import { CarrierConnectionConfig } from 'models/CarrierConnectionConfig';
import { getCarrierConnectionConfig, getCarrierDisplayName } from 'services/carrierService';
import { useAuth } from './useAuth';

const useCarrierConnectionConfig = (enabled = true) => {
  const { accessToken } = useAuth();
  const [carrierConfigs, setCarrierConfigs] = useState<CarrierConnectionConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !accessToken) return;

    let ignore = false;
    const loadConfigs = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getCarrierConnectionConfig(accessToken);
        if (!ignore) setCarrierConfigs(data);
      } catch (err: unknown) {
        if (!ignore) {
          setError(err instanceof Error ? err.message : 'Failed to load carrier config');
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadConfigs();
    return () => {
      ignore = true;
    };
  }, [enabled, accessToken]);

  const resolveDisplayName = useCallback(
    (carrierName?: string | null) =>
      getCarrierDisplayName(carrierName ?? '', carrierConfigs),
    [carrierConfigs],
  );

  return { carrierConfigs, loading, error, resolveDisplayName };
};

export default useCarrierConnectionConfig;
